<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@chatwoot/pico-search';

import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import Input from 'next/input/Input.vue';
import McpToolCard from './McpToolCard.vue';
import CreateToolDialog from './create/CreateToolDialog.vue';
import DeleteToolDialog from './delete/DeleteToolDialog.vue';
import ExecutionLogDialog from './ExecutionLogDialog.vue';

const props = defineProps({
  server: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const createToolDialogRef = ref(null);
const deleteToolDialogRef = ref(null);
const executionLogDialogRef = ref(null);
const selectedTool = ref({});
const searchQuery = ref('');

const uiFlags = useMapGetter('wgptMcpServers/getUIFlags');
const tools = computed(() =>
  store.getters['wgptMcpServers/getTools'](props.server.id)
);

const filteredTools = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return tools.value;
  return picoSearch(tools.value, query, ['name', 'description']);
});

const summary = computed(() => ({
  active: tools.value.filter(tool => tool.active).length,
  total: tools.value.length,
}));

const handleEditTool = tool => {
  router.push({
    name: 'wgpt_mcp_servers_edit_tool',
    params: { ...route.params, toolId: tool.id },
    query: { selected: 'settings' },
  });
};

const handleDeleteTool = tool => {
  selectedTool.value = tool;
  deleteToolDialogRef.value?.open();
};

const handleCloneTool = async tool => {
  try {
    await store.dispatch('wgptMcpServers/cloneTool', {
      serverId: props.server.id,
      id: tool.id,
    });
    useAlert(t('WGPT_MCP_SERVERS.EDIT.TOOLS.CLONE_TOOL.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_MCP_SERVERS.EDIT.TOOLS.CLONE_TOOL.API.ERROR_MESSAGE')
    );
  }
};

const handleToolToggle = async (tool, isActive) => {
  try {
    await store.dispatch('wgptMcpServers/updateTool', {
      serverId: props.server.id,
      id: tool.id,
      active: isActive,
    });
    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const successMessage = t(
      `WGPT_MCP_SERVERS.EDIT.TOOLS.EDIT_TOOL.API.${isActive ? 'ACTIVATED' : 'DEACTIVATED'}_SUCCESSFUL`
    );
    useAlert(successMessage);
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDIT_TOOL.API.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="mx-6 min-h-full">
    <div class="py-4">
      <h3 class="text-lg font-medium text-n-slate-12">
        {{ t('WGPT_MCP_SERVERS.EDIT.TOOLS.TITLE') }}
      </h3>
      <p class="text-sm text-n-slate-11 mt-1">
        {{ t('WGPT_MCP_SERVERS.EDIT.TOOLS.DESCRIPTION') }}
      </p>
    </div>

    <div class="flex items-center justify-end w-full pb-4 gap-2">
      <span class="text-xs font-medium text-n-slate-10">
        {{
          t('WGPT_MCP_SERVERS.EDIT.TOOLS.SUMMARY', {
            active: summary.active,
            total: summary.total,
          })
        }}
      </span>
      <div class="h-4 w-px bg-n-strong" />
      <Button
        :label="t('WGPT_MCP_SERVERS.EDIT.TOOLS.HEADER.EXECUTION_LOG')"
        icon="i-lucide-scroll-text"
        color="slate"
        size="sm"
        @click="executionLogDialogRef?.open()"
      />
      <Button
        :label="t('WGPT_MCP_SERVERS.EDIT.TOOLS.HEADER.CREATE_TOOL')"
        :is-loading="uiFlags.isUpdating"
        :disabled="uiFlags.isUpdating"
        icon="i-lucide-plus"
        size="sm"
        @click="createToolDialogRef?.open()"
      />
    </div>

    <div
      v-if="tools.length"
      class="outline outline-1 -outline-offset-1 outline-n-weak rounded-xl mb-8 overflow-hidden"
    >
      <div v-if="tools.length > 3" class="px-6 py-4 border-b border-n-weak">
        <Input
          v-model="searchQuery"
          :placeholder="t('WGPT_MCP_SERVERS.EDIT.TOOLS.SEARCH_PLACEHOLDER')"
          class="group w-52 min-w-0 [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8 [&>input]:!rounded-[0.625rem]"
          size="sm"
          type="search"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-3.5 ltr:left-2.5 rtl:right-2.5"
            />
          </template>
        </Input>
      </div>
      <div class="flex flex-col">
        <McpToolCard
          v-for="(tool, index) of filteredTools"
          :key="tool.id"
          :tool="tool"
          :class="{
            'border-b border-n-weak': index < filteredTools.length - 1,
          }"
          @edit="handleEditTool(tool)"
          @clone="handleCloneTool(tool)"
          @delete="handleDeleteTool(tool)"
          @toggle-active="isActive => handleToolToggle(tool, isActive)"
        />
        <p
          v-if="searchQuery && !filteredTools.length"
          class="text-body-main text-n-slate-11 text-center py-6"
        >
          {{ t('WGPT_MCP_SERVERS.EDIT.TOOLS.NO_RESULTS') }}
        </p>
      </div>
    </div>
    <div
      v-else
      class="flex flex-col items-center justify-center gap-3 py-16 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl mb-8"
    >
      <Icon icon="i-lucide-wrench" class="size-8 text-n-slate-9" />
      <p class="text-heading-3 text-n-slate-12">
        {{ t('WGPT_MCP_SERVERS.EDIT.TOOLS.EMPTY.TITLE') }}
      </p>
      <p class="text-sm text-n-slate-11 text-center max-w-md">
        {{ t('WGPT_MCP_SERVERS.EDIT.TOOLS.EMPTY.DESCRIPTION') }}
      </p>
    </div>

    <CreateToolDialog ref="createToolDialogRef" :server="server" />

    <DeleteToolDialog
      ref="deleteToolDialogRef"
      :server="server"
      :tool="selectedTool"
    />

    <ExecutionLogDialog
      ref="executionLogDialogRef"
      :server-id="server.id"
      :tools="tools"
    />
  </div>
</template>
